<script setup>
import { ref, computed, onMounted } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import axios from '@axios'
import { useUsersStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'
import CmsText from '@core/components/CmsText.vue'
import RestaurantMemberFeedbackPanel from '@core/components/RestaurantMemberFeedbackPanel.vue'

const route = useRoute()
const router = useRouter()
const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)
const { dialogWidth } = useDesignSystem()

const memberUserId = computed(() => route.params.memberUserId)

const detail = ref(null)
const loading = ref(false)
const error = ref(null)

// Preferred mutation state
const preferredMutating = ref(false)
const preferredError = ref(null)

// Scope restaurants (for preferred controls)
const scopeRestaurants = ref([])
const eligibleGroups = ref([])

const isPlatformAdmin = computed(() => myUser.value?.userLevel === 'admin')

// Derived from detail
const profile = computed(() => detail.value?.profile || null)
const favoritedRestaurants = computed(() => detail.value?.favoritedRestaurants || [])
const bookedRestaurants = computed(() => detail.value?.bookedRestaurants || [])
const preferredAt = computed(() => detail.value?.preferredAt || [])
const bookingHistory = computed(() => detail.value?.bookingHistory || [])
const messagingConversationIds = computed(() => detail.value?.messagingConversationIds || [])
const guestNotes = computed(() => detail.value?.guestNotes || [])

// Add to Profile: shares the member approved for the caller's restaurants
// (their own words, read-only). Private entries never reach this page.
const memberProfileShares = computed(() => detail.value?.memberProfileShares || [])
function highlightRequestLabel(share) {
  const st = share.publicRequest?.state
  if (st === 'requested') return 'Asked the member'
  if (st === 'approved') return 'Public Member Highlight'
  if (st === 'declined') return 'Member said no'

  return ''
}

function shareDeliveryLabel(share) {
  if (share.status === 'withdrawn') return 'Withdrawn by the member'
  const st = share.delivery?.state
  if (st === 'relayed') return `Passed along ${share.delivery?.relayedAt ? formatDate(share.delivery.relayedAt) : ''}`.trim()
  if (st === 'not_relayed') return 'Not passed along'

  return 'Awaiting RM relay'
}

const memberInitials = computed(() => {
  if (!profile.value) return '?'
  const f = profile.value.firstName?.[0] || ''
  const l = profile.value.lastName?.[0] || ''
  
  return (f + l).toUpperCase() || '?'
})

function favoriteForRestaurant(restaurantId) {
  return favoritedRestaurants.value.some(
    f => f._id?.toString() === restaurantId?.toString(),
  )
}

function bookingForRestaurant(restaurantId) {
  return bookedRestaurants.value.find(
    br => br._id?.toString() === restaurantId?.toString(),
  ) || null
}

function isPreferredAtRestaurant(restaurantId) {
  return preferredAt.value.some(
    p => p.scope === 'restaurant' && p.restaurantId?.toString() === restaurantId?.toString(),
  )
}

function isPreferredViaGroup(restaurantId, groupId) {
  return preferredAt.value.some(
    p => p.scope === 'group' && p.userGroupId?.toString() === groupId?.toString(),
  )
}

function getPreferredRecord(restaurantId) {
  return preferredAt.value.find(
    p => p.scope === 'restaurant' && p.restaurantId?.toString() === restaurantId?.toString(),
  )
}

function getGroupPreferredRecord(groupId) {
  return preferredAt.value.find(
    p => p.scope === 'group' && p.userGroupId?.toString() === groupId?.toString(),
  )
}

// Single eligible group (same spec as AdminEditUserDialog §7)
const singleEligibleGroup = computed(() => {
  return eligibleGroups.value.length === 1 ? eligibleGroups.value[0] : null
})

async function loadDetail() {
  loading.value = true
  error.value = null
  try {
    const { data } = await axios.get(`/restaurant-admin/fans/${memberUserId.value}`)
    detail.value = data
    syncNoteDrafts()
  } catch (err) {
    error.value = err?.response?.data?.error || 'Failed to load fan details'
  } finally {
    loading.value = false
  }
}

async function loadPreferredScope() {
  try {
    const { data } = await axios.get('/user/me/restaurant-scope')
    scopeRestaurants.value = Array.isArray(data?.restaurants) ? data.restaurants : []
    const groups = Array.isArray(data?.userGroups) ? data.userGroups : []
    eligibleGroups.value = groups.filter(
      g => g?.type === 'restaurant' || g?.type === 'restaurantGroup',
    )
  } catch {
    scopeRestaurants.value = []
    eligibleGroups.value = []
  }
}

async function markPreferred(restaurantId) {
  preferredMutating.value = true
  preferredError.value = null
  try {
    await axios.post('/preferred-member/restaurant', {
      memberUserId: memberUserId.value,
      restaurantId,
    })
    await loadDetail()
  } catch (err) {
    preferredError.value = err?.response?.data?.error || 'Failed to mark as Preferred'
  } finally {
    preferredMutating.value = false
  }
}

async function removePreferred(preferredId) {
  preferredMutating.value = true
  preferredError.value = null
  try {
    await axios.delete(`/preferred-member/${preferredId}`)
    await loadDetail()
  } catch (err) {
    preferredError.value = err?.response?.data?.error || 'Failed to remove Preferred status'
  } finally {
    preferredMutating.value = false
  }
}

async function markPreferredGroup(userGroupId) {
  preferredMutating.value = true
  preferredError.value = null
  try {
    await axios.post('/preferred-member/group', {
      memberUserId: memberUserId.value,
      userGroupId,
    })
    await loadDetail()
  } catch (err) {
    preferredError.value = err?.response?.data?.error || 'Failed to mark as Preferred across group'
  } finally {
    preferredMutating.value = false
  }
}

function statusColor(status) {
  if (['Accepted', 'Experienced'].includes(status)) return 'success'
  if (['Declined', 'Not Available', 'No Response', 'Rescinded', 'auto_declined'].includes(status)) return 'error'
  if (status === 'Canceled') return 'warning'
  
  return 'default'
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return dateStr
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return ''
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

const lastActiveFormatted = computed(() => {
  if (!profile.value?.lastActiveAt) return null
  
  return formatDate(profile.value.lastActiveAt)
})

function goToConversation(conversationId) {
  router.push(`/admin/messaging/${conversationId}`)
}

// ─── Messaging Composer ───────────────────────────────────────────────────────

const messageDialogOpen = ref(false)
const messageText = ref('')
const selectedRestaurantId = ref(null)
const messageSending = ref(false)
const messageError = ref(null)

// Restaurants in scope that this fan has favorited — eligible for initiating a conversation
const messagingEligibleRestaurants = computed(() => {
  const favIds = new Set((favoritedRestaurants.value || []).map(r => r._id?.toString()))
  
  return (scopeRestaurants.value || []).filter(r => favIds.has(r._id?.toString()))
})

function openMessageDialog() {
  messageText.value = ''
  messageError.value = null
  selectedRestaurantId.value = messagingEligibleRestaurants.value.length === 1
    ? messagingEligibleRestaurants.value[0]._id
    : null
  messageDialogOpen.value = true
}

async function sendMessage() {
  if (!messageText.value.trim()) return
  const restaurantId = selectedRestaurantId.value
  if (!restaurantId) {
    messageError.value = 'Please select a restaurant to send from.'
    
    return
  }
  messageSending.value = true
  messageError.value = null
  try {
    await axios.post('/messaging/restaurantAdmin/conversations', {
      memberUserId: memberUserId.value,
      restaurantId,
      text: messageText.value.trim(),
    })
    messageDialogOpen.value = false

    // Refresh so the new conversation appears in the Messages card.
    loadDetail()
  } catch (err) {
    messageError.value = err?.response?.data?.message || 'Failed to send message'
  } finally {
    messageSending.value = false
  }
}

// ─── Guest Notes ──────────────────────────────────────────────────────────────
// Restaurant-visible notes a team member keeps about this fan. Each note is
// scoped to a single restaurant/member relationship. Never shown to the member.
//
// UX: default to a list of *existing* notes (read-only previews). Editing is
// progressive disclosure — click a row to edit, or use the "Add note" menu to
// open an editor for a restaurant that doesn't have a note yet.

const GUEST_NOTE_MAX_LENGTH = 2000

// Per-restaurant editable draft text, keyed by restaurantId.
const noteDrafts = ref({})
const noteSaving = ref({})
const noteError = ref({})

// Which restaurant rows are currently open in editor mode.
const editingNotes = ref({})
const addNoteMenu = ref(false)

function guestNoteFor(restaurantId) {
  return guestNotes.value.find(
    n => n.restaurantId?.toString() === restaurantId?.toString(),
  ) || null
}

// Reset drafts to the saved note text whenever fresh detail loads.
function syncNoteDrafts() {
  const drafts = {}
  favoritedRestaurants.value.forEach(r => {
    drafts[r._id] = guestNoteFor(r._id)?.note || ''
  })
  noteDrafts.value = drafts
}

function noteHasChange(restaurantId) {
  const draft = (noteDrafts.value[restaurantId] || '').trim()
  const saved = guestNoteFor(restaurantId)?.note || ''
  
  return draft !== saved
}

// Rows that should be visible in the notes list: any restaurant with a saved
// note OR one that's currently being edited (e.g. a newly opened blank row).
const visibleNoteRows = computed(() => {
  const out = []
  const seen = new Set()
  favoritedRestaurants.value.forEach(r => {
    const key = r._id?.toString()
    if (!key || seen.has(key)) return
    if (editingNotes.value[r._id] || guestNoteFor(r._id)) {
      out.push(r)
      seen.add(key)
    }
  })
  
  return out
})

// Restaurants the fan favorited that have no row yet — surfaced by the
// "Add note" affordance so the admin can pick where to start writing.
const restaurantsWithoutVisibleNote = computed(() => favoritedRestaurants.value.filter(
  r => !editingNotes.value[r._id] && !guestNoteFor(r._id),
))

function startEditingNote(restaurantId) {
  noteDrafts.value = {
    ...noteDrafts.value,
    [restaurantId]: guestNoteFor(restaurantId)?.note || '',
  }
  noteError.value = { ...noteError.value, [restaurantId]: null }
  editingNotes.value = { ...editingNotes.value, [restaurantId]: true }
}

function cancelEditingNote(restaurantId) {
  noteDrafts.value = {
    ...noteDrafts.value,
    [restaurantId]: guestNoteFor(restaurantId)?.note || '',
  }
  noteError.value = { ...noteError.value, [restaurantId]: null }
  editingNotes.value = { ...editingNotes.value, [restaurantId]: false }
}

function beginAddingNoteFor(restaurantId) {
  startEditingNote(restaurantId)
  addNoteMenu.value = false
}

async function saveGuestNote(restaurantId) {
  noteSaving.value = { ...noteSaving.value, [restaurantId]: true }
  noteError.value = { ...noteError.value, [restaurantId]: null }
  try {
    await axios.put(`/restaurant-admin/fans/${memberUserId.value}/notes`, {
      restaurantId,
      note: noteDrafts.value[restaurantId] || '',
    })
    await loadDetail()
    editingNotes.value = { ...editingNotes.value, [restaurantId]: false }
  } catch (err) {
    noteError.value = {
      ...noteError.value,
      [restaurantId]: err?.response?.data?.error || 'Failed to save note',
    }
  } finally {
    noteSaving.value = { ...noteSaving.value, [restaurantId]: false }
  }
}

async function removeGuestNote(restaurantId) {
  noteDrafts.value = { ...noteDrafts.value, [restaurantId]: '' }
  await saveGuestNote(restaurantId)
}

onMounted(async () => {
  await Promise.all([loadDetail(), loadPreferredScope()])
})
</script>

<template>
  <div class="fan-detail">
    <!-- Back nav -->
    <div class="fan-detail__back">
      <VBtn
        variant="text"
        prepend-icon="mdi-arrow-left"
        size="small"
        @click="router.back()"
      >
        Back to Fans
      </VBtn>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="fan-detail__loading"
    >
      <VProgressCircular
        indeterminate
        color="primary"
        size="32"
      />
    </div>

    <!-- Error -->
    <VAlert
      v-else-if="error"
      type="error"
      variant="tonal"
      class="ma-4"
    >
      {{ error }}
    </VAlert>

    <!-- Content -->
    <div
      v-else-if="detail"
      class="fan-detail__content"
    >
      <div class="fan-detail__col fan-detail__col--left">
        <!-- 1. Profile Card -->
        <VCard
          class="fan-detail__card"
          variant="outlined"
        >
          <VCardText class="fan-detail__profile">
            <VAvatar
              :size="64"
              :color="profile.profileImageId ? undefined : 'primary-light'"
              class="fan-detail__profile-avatar"
            >
              <VImg
                v-if="profile.profileImageId"
                :src="mediaUrl(profile.profileImageId, 'regular')"
                :alt="profile.firstName"
              />
              <span
                v-else
                class="fan-detail__profile-initials"
                style="font-size: 22px; color: var(--ds-brand-primary-dark);"
              >
                {{ memberInitials }}
              </span>
            </VAvatar>

            <div class="fan-detail__profile-info">
              <div class="fan-detail__profile-header">
                <div class="fan-detail__profile-identity">
                  <div class="fan-detail__profile-name">
                    {{ profile.firstName }} {{ profile.lastName }}
                  </div>
                  <div
                    v-if="profile.company || profile.title"
                    class="fan-detail__profile-subtitle"
                  >
                    {{ [profile.title, profile.company].filter(Boolean).join(' · ') }}
                  </div>
                </div>
                <div class="fan-detail__profile-actions">
                  <VTooltip
                    :text="messagingEligibleRestaurants.length === 0
                      ? 'This member hasn\'t favorited any of your restaurants yet.'
                      : undefined"
                    :disabled="messagingEligibleRestaurants.length > 0"
                  >
                    <template #activator="{ props }">
                      <span v-bind="props">
                        <VBtn
                          color="primary"
                          variant="flat"
                          prepend-icon="mdi-message-plus-outline"
                          size="small"
                          :disabled="messagingEligibleRestaurants.length === 0"
                          @click="openMessageDialog"
                        >
                          Message
                        </VBtn>
                      </span>
                    </template>
                  </VTooltip>
                </div>
              </div>
              <div class="fan-detail__profile-chips">
                <VChip
                  v-if="profile.isPlatinum"
                  size="small"
                  color="primary"
                  variant="tonal"
                >
                  Platinum
                </VChip>
                <VChip
                  v-for="tag in (profile.verifiedTagNames || [])"
                  :key="tag"
                  size="small"
                  variant="tonal"
                  color="secondary"
                >
                  {{ tag }}
                </VChip>
              </div>
              <!-- Social links -->
              <div class="fan-detail__social">
                <a
                  v-if="profile.profileLinkedInProfileId"
                  :href="`https://linkedin.com/in/${profile.profileLinkedInProfileId}`"
                  target="_blank"
                  rel="noopener"
                  class="fan-detail__social-link"
                >
                  <VIcon size="18">mdi-linkedin</VIcon>
                </a>
                <a
                  v-if="profile.profileInstagramProfileId"
                  :href="`https://instagram.com/${profile.profileInstagramProfileId}`"
                  target="_blank"
                  rel="noopener"
                  class="fan-detail__social-link"
                >
                  <VIcon size="18">mdi-instagram</VIcon>
                </a>
                <a
                  v-if="profile.profileWebsiteLink"
                  :href="profile.profileWebsiteLink"
                  target="_blank"
                  rel="noopener"
                  class="fan-detail__social-link"
                >
                  <VIcon size="18">mdi-web</VIcon>
                </a>
              </div>
              <div
                v-if="profile.bio"
                class="fan-detail__profile-bio"
              >
                {{ profile.bio }}
              </div>
              <div
                v-if="lastActiveFormatted"
                class="fan-detail__profile-meta"
              >
                Last active: {{ lastActiveFormatted }}
              </div>
            </div>
          </VCardText>
        </VCard>

        <!-- 3. Preferred Controls -->
        <VCard
          class="fan-detail__card"
          variant="outlined"
        >
          <VCardTitle class="fan-detail__card-title">
            Preferred Member Status
          </VCardTitle>
          <VCardText>
            <VAlert
              v-if="preferredError"
              type="error"
              variant="tonal"
              density="compact"
              closable
              class="mb-3"
              @click:close="preferredError = null"
            >
              {{ preferredError }}
            </VAlert>

            <div
              v-for="r in scopeRestaurants"
              :key="r._id"
              class="fan-detail__preferred-row"
            >
              <div class="fan-detail__preferred-info">
                <div
                  class="fan-detail__preferred-rest-name"
                  style="font-size: 16px;"
                >
                  {{ r.name }}
                </div>
                <div
                  v-if="favoriteForRestaurant(r._id) || bookingForRestaurant(r._id)"
                  class="fan-detail__preferred-engagement"
                >
                  <span
                    v-if="favoriteForRestaurant(r._id)"
                    class="fan-detail__preferred-engagement-item"
                  >
                    <VIcon
                      size="12"
                      color="error"
                    >mdi-heart</VIcon>
                    Favorited
                  </span>
                  <span
                    v-if="bookingForRestaurant(r._id)"
                    class="fan-detail__preferred-engagement-item"
                  >
                    <VIcon size="12">mdi-calendar-check-outline</VIcon>
                    Booked {{ bookingForRestaurant(r._id).count }}×
                  </span>
                </div>
                <div
                  v-if="singleEligibleGroup && isPreferredViaGroup(r._id, singleEligibleGroup._id)"
                  class="fan-detail__preferred-via"
                >
                  Via {{ singleEligibleGroup.name }}
                </div>
              </div>
              <div class="fan-detail__preferred-actions">
                <VChip
                  :color="isPreferredAtRestaurant(r._id) ? 'primary' : 'default'"
                  :variant="isPreferredAtRestaurant(r._id) ? 'flat' : 'tonal'"
                  size="small"
                >
                  {{ isPreferredAtRestaurant(r._id) ? 'Preferred' : 'Not Preferred' }}
                </VChip>
                <VBtn
                  v-if="!isPreferredAtRestaurant(r._id)"
                  size="small"
                  color="primary"
                  variant="outlined"
                  :loading="preferredMutating"
                  :disabled="singleEligibleGroup && isPreferredViaGroup(r._id, singleEligibleGroup._id)"
                  @click="markPreferred(r._id)"
                >
                  Mark Preferred
                </VBtn>
                <VBtn
                  v-else
                  size="small"
                  color="error"
                  variant="outlined"
                  :loading="preferredMutating"
                  @click="removePreferred(getPreferredRecord(r._id)?.preferredId)"
                >
                  Remove
                </VBtn>
              </div>
            </div>

            <!-- Group-level assignment (only when caller has exactly one eligible group) -->
            <div
              v-if="singleEligibleGroup"
              class="fan-detail__preferred-row fan-detail__preferred-row--group"
            >
              <div class="fan-detail__preferred-info">
                <div
                  class="fan-detail__preferred-rest-name"
                  style="font-size: 16px;"
                >
                  {{ getGroupPreferredRecord(singleEligibleGroup._id)
                    ? `Preferred across ${singleEligibleGroup.name}`
                    : `Mark as Preferred across ${singleEligibleGroup.name}` }}
                </div>
                <div class="fan-detail__preferred-via">
                  Applies to all restaurants in this group
                </div>
              </div>
              <div class="fan-detail__preferred-actions">
                <VChip
                  :color="getGroupPreferredRecord(singleEligibleGroup._id) ? 'primary' : 'default'"
                  :variant="getGroupPreferredRecord(singleEligibleGroup._id) ? 'flat' : 'tonal'"
                  size="small"
                >
                  {{ getGroupPreferredRecord(singleEligibleGroup._id) ? 'Preferred' : 'Not Preferred' }}
                </VChip>
                <VBtn
                  v-if="!getGroupPreferredRecord(singleEligibleGroup._id)"
                  size="small"
                  color="primary"
                  variant="outlined"
                  :loading="preferredMutating"
                  @click="markPreferredGroup(singleEligibleGroup._id)"
                >
                  Mark Preferred
                </VBtn>
                <VBtn
                  v-else
                  size="small"
                  color="error"
                  variant="outlined"
                  :loading="preferredMutating"
                  @click="removePreferred(getGroupPreferredRecord(singleEligibleGroup._id)?.preferredId)"
                >
                  Remove
                </VBtn>
              </div>
            </div>
          </VCardText>
        </VCard>
      </div>

      <div class="fan-detail__col fan-detail__col--right">
        <!-- Per-restaurant engagement is now inline in the Preferred card (left col). -->

        <!-- 3b. Guest Notes — read-first, progressive disclosure to edit -->
        <VCard
          v-if="favoritedRestaurants.length > 0"
          class="fan-detail__card"
          variant="outlined"
        >
          <div class="fan-detail__notes-head">
            <VCardTitle class="fan-detail__card-title fan-detail__notes-title">
              Guest notes
            </VCardTitle>
            <div class="fan-detail__notes-head-action">
              <VBtn
                v-if="restaurantsWithoutVisibleNote.length === 1"
                variant="text"
                color="primary"
                size="small"
                prepend-icon="mdi-plus"
                @click="beginAddingNoteFor(restaurantsWithoutVisibleNote[0]._id)"
              >
                Add note
              </VBtn>
              <VMenu
                v-else-if="restaurantsWithoutVisibleNote.length > 1"
                v-model="addNoteMenu"
                location="bottom end"
              >
                <template #activator="{ props }">
                  <VBtn
                    v-bind="props"
                    variant="text"
                    color="primary"
                    size="small"
                    prepend-icon="mdi-plus"
                    append-icon="mdi-menu-down"
                  >
                    Add note
                  </VBtn>
                </template>
                <VList
                  density="compact"
                  min-width="220"
                >
                  <VListItem
                    v-for="r in restaurantsWithoutVisibleNote"
                    :key="r._id"
                    :title="r.name"
                    @click="beginAddingNoteFor(r._id)"
                  />
                </VList>
              </VMenu>
            </div>
          </div>
          <VCardText class="fan-detail__notes-body">
            <p class="fan-detail__notes-sub">
              Visible only to your team — never shown to the member.
            </p>

            <p
              v-if="visibleNoteRows.length === 0"
              class="fan-detail__notes-empty"
            >
              No guest notes yet.
            </p>

            <div
              v-else
              class="fan-detail__notes-list"
            >
              <div
                v-for="r in visibleNoteRows"
                :key="r._id"
                class="fan-detail__note-row"
              >
                <!-- EDIT MODE -->
                <div
                  v-if="editingNotes[r._id]"
                  class="fan-detail__note-edit"
                >
                  <div class="fan-detail__note-restaurant">
                    {{ r.name }}
                  </div>
                  <VTextarea
                    v-model="noteDrafts[r._id]"
                    :counter="GUEST_NOTE_MAX_LENGTH"
                    :maxlength="GUEST_NOTE_MAX_LENGTH"
                    rows="3"
                    auto-grow
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    placeholder="Add a note about this guest…"
                    autofocus
                    style="font-size: 16px;"
                  />
                  <VAlert
                    v-if="noteError[r._id]"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                  >
                    {{ noteError[r._id] }}
                  </VAlert>
                  <div class="fan-detail__note-edit-actions">
                    <div class="fan-detail__note-meta">
                      <template v-if="guestNoteFor(r._id)">
                        Last updated by
                        {{ guestNoteFor(r._id).updatedByName || 'a team member' }}
                        · {{ formatDateTime(guestNoteFor(r._id).updatedAt) }}
                      </template>
                    </div>
                    <div class="fan-detail__note-edit-buttons">
                      <VBtn
                        v-if="guestNoteFor(r._id)"
                        variant="text"
                        color="error"
                        size="small"
                        :disabled="noteSaving[r._id]"
                        @click="removeGuestNote(r._id)"
                      >
                        Remove
                      </VBtn>
                      <VBtn
                        variant="text"
                        size="small"
                        :disabled="noteSaving[r._id]"
                        @click="cancelEditingNote(r._id)"
                      >
                        Cancel
                      </VBtn>
                      <VBtn
                        color="primary"
                        variant="flat"
                        size="small"
                        :loading="noteSaving[r._id]"
                        :disabled="!noteHasChange(r._id) || !(noteDrafts[r._id] || '').trim()"
                        @click="saveGuestNote(r._id)"
                      >
                        Save
                      </VBtn>
                    </div>
                  </div>
                </div>

                <!-- READ MODE -->
                <button
                  v-else
                  type="button"
                  class="fan-detail__note-read"
                  @click="startEditingNote(r._id)"
                >
                  <div class="fan-detail__note-read-head">
                    <span class="fan-detail__note-restaurant">{{ r.name }}</span>
                    <span class="fan-detail__note-meta">
                      {{ guestNoteFor(r._id).updatedByName || 'a team member' }}
                      · {{ formatDateTime(guestNoteFor(r._id).updatedAt) }}
                    </span>
                  </div>
                  <div class="fan-detail__note-read-body">
                    {{ guestNoteFor(r._id).note }}
                  </div>
                </button>
              </div>
            </div>
          </VCardText>
        </VCard>

        <!-- 3c. Shared by the member (Add to Profile) — read-only, their words -->
        <VCard
          v-if="memberProfileShares.length > 0"
          class="fan-detail__card"
          variant="outlined"
        >
          <!--
            The restaurant sees the member's words and when; RM's own steps
            (passed along, asked the member) are for RM only (Kapil, 2026-09-24).
          -->
          <VCardTitle class="fan-detail__card-title">
            Member Highlights
          </VCardTitle>
          <VCardText class="fan-detail__notes-body">
            <p class="fan-detail__notes-sub">
              Written by the member and approved for your team.<template v-if="isPlatformAdmin">
                RM passes each one along by hand.
              </template>
            </p>
            <div class="fan-detail__notes-list">
              <div
                v-for="s in memberProfileShares"
                :key="s.shareId"
                class="fan-detail__note-row"
              >
                <div class="fan-detail__share-restaurant">
                  {{ s.restaurantName || 'Restaurant' }}
                </div>
                <div class="fan-detail__share-text">
                  {{ s.text }}
                </div>
                <div class="fan-detail__share-meta">
                  {{ formatDate(s.sharedAt) }}
                  <!-- RM passes highlights along and asks members from one screen. -->
                  <template v-if="isPlatformAdmin">
                    · {{ shareDeliveryLabel(s) }}
                    <template v-if="s.publicRequest">
                      · {{ highlightRequestLabel(s) }}
                    </template>
                    ·
                    <RouterLink to="/admin-member-highlights">
                      Member Highlights
                    </RouterLink>
                  </template>
                </div>
              </div>
            </div>
          </VCardText>
        </VCard>

        <!-- 4. Booking History -->
        <VCard
          v-if="bookingHistory.length > 0"
          class="fan-detail__card"
          variant="outlined"
        >
          <VCardTitle class="fan-detail__card-title">
            Booking history
          </VCardTitle>
          <VCardText class="pa-0">
            <div
              v-for="res in bookingHistory"
              :key="res._id"
              class="fan-detail__booking-row"
            >
              <div class="fan-detail__booking-left">
                <div class="fan-detail__booking-date">
                  {{ formatDate(res.date) }}
                </div>
                <div class="fan-detail__booking-rest">
                  {{ res.confirmedRestaurant || res.preferredRestaurant }}
                </div>
                <div class="fan-detail__booking-meta">
                  Party of {{ res.partySize }}
                  <template v-if="res.minSpendPerPerson">
                    · ${{ res.minSpendPerPerson }}/person
                  </template>
                </div>
              </div>
              <div class="fan-detail__booking-right">
                <VChip
                  :color="statusColor(res.status)"
                  variant="tonal"
                  size="x-small"
                >
                  {{ res.status }}
                </VChip>
                <VIcon
                  v-if="res.preferredMemberBypassApplied"
                  size="16"
                  color="primary"
                  title="Preferred member bypass applied"
                >
                  mdi-star-circle
                </VIcon>
              </div>
            </div>
          </VCardText>
        </VCard>

        <!-- 4b. Member Feedback per scope restaurant (RM-140) -->
        <VCard
          v-if="scopeRestaurants.length > 0"
          class="fan-detail__card"
          variant="outlined"
        >
          <VCardTitle class="fan-detail__card-title">
            Member feedback
          </VCardTitle>
          <VCardText>
            <div
              v-for="r in scopeRestaurants"
              :key="`feedback-${r._id}`"
              class="fan-detail__feedback-row"
            >
              <div class="text-subtitle-2 mb-1">
                {{ r.name }}
              </div>
              <RestaurantMemberFeedbackPanel
                :restaurant-id="String(r._id)"
                :member-user-id="String(memberUserId)"
              />
            </div>
          </VCardText>
        </VCard>

        <!-- 5. Messaging Card -->
        <VCard
          class="fan-detail__card"
          variant="outlined"
        >
          <VCardTitle class="fan-detail__card-title">
            Messages
          </VCardTitle>
          <VCardText class="pa-0">
            <VListItem
              v-for="conv in messagingConversationIds"
              :key="conv.conversationId"
              :subtitle="conv.lastMessageAt ? formatDate(conv.lastMessageAt) : undefined"
              class="fan-detail__conv-item"
              @click="goToConversation(conv.conversationId)"
            >
              <template #prepend>
                <VIcon color="primary">
                  mdi-message-outline
                </VIcon>
              </template>
              <template #append>
                <VIcon
                  size="16"
                  color="medium-emphasis"
                >
                  mdi-chevron-right
                </VIcon>
              </template>
            </VListItem>
            <div
              v-if="messagingConversationIds.length === 0"
              class="fan-detail__messaging-empty"
            >
              No messages yet.
            </div>
          </VCardText>
        </VCard>
      </div>
    </div>

    <!-- Message Composer Dialog -->
    <VDialog
      v-model="messageDialogOpen"
      :width="dialogWidth('sm')"
      :max-width="500"
    >
      <VCard>
        <VCardTitle class="fan-detail__card-title pt-4 px-4">
          Send message to {{ profile?.firstName }} {{ profile?.lastName }}
        </VCardTitle>
        <VCardText>
          <!-- Send-as selector (only shown when multiple eligible restaurants) -->
          <VSelect
            v-if="messagingEligibleRestaurants.length > 1"
            v-model="selectedRestaurantId"
            :items="messagingEligibleRestaurants"
            item-title="name"
            item-value="_id"
            label="Send as"
            hint="Your message will appear from this restaurant's name."
            persistent-hint
            class="mb-4"
            style="font-size: 16px;"
          />

          <VTextarea
            v-model="messageText"
            label="Message"
            :counter="2000"
            :maxlength="2000"
            rows="4"
            auto-grow
            style="font-size: 16px;"
          />

          <VAlert
            v-if="messageError"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ messageError }}
          </VAlert>
        </VCardText>
        <VCardActions class="px-4 pb-4">
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="messageSending"
            @click="messageDialogOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            :loading="messageSending"
            :disabled="!messageText.trim() || !selectedRestaurantId"
            @click="sendMessage"
          >
            Send
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.fan-detail {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--ds-space-4, 16px);
}

.fan-detail__back {
  margin-bottom: var(--ds-space-4, 16px);
}

.fan-detail__loading {
  display: flex;
  justify-content: center;
  padding: var(--ds-space-16, 64px);
}

.fan-detail__content {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--ds-space-4, 16px);
  align-items: start;
}

.fan-detail__col {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4, 16px);
  min-width: 0;
}

@media (min-width: 960px) {
  .fan-detail__content {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

.fan-detail__card {
  border-radius: var(--ds-radius-lg, 12px);
}

.fan-detail__card-title {
  font-size: 16px;
  font-weight: 600;
  padding: var(--ds-space-4, 16px) var(--ds-space-4, 16px) var(--ds-space-2, 8px);
}

/* Profile card */
.fan-detail__profile {
  display: flex;
  gap: var(--ds-space-4, 16px);
  align-items: flex-start;
  padding: var(--ds-space-5, 20px);
}

.fan-detail__profile-avatar {
  flex-shrink: 0;
}

.fan-detail__profile-initials {
  font-weight: 700;
  letter-spacing: 0.5px;
}

.fan-detail__profile-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2, 8px);
}

.fan-detail__profile-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
  flex-wrap: wrap;
}

.fan-detail__profile-identity {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-1, 4px);
}

.fan-detail__profile-actions {
  flex-shrink: 0;
}

.fan-detail__profile-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--ds-text-strong, inherit);
  line-height: 1.2;
}

.fan-detail__profile-subtitle {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.fan-detail__profile-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-space-1, 4px);
}

.fan-detail__social {
  display: flex;
  gap: var(--ds-space-3, 12px);
  align-items: center;
}

.fan-detail__social-link {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  text-decoration: none;
  line-height: 1;
}

.fan-detail__social-link:hover {
  color: var(--ds-brand-primary-dark);
}

.fan-detail__profile-bio {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  line-height: 1.5;
}

.fan-detail__profile-meta {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin-top: var(--ds-space-1, 4px);
}

/* Preferred rows */
.fan-detail__preferred-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
  padding: var(--ds-space-2, 8px) 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
}

.fan-detail__preferred-row:last-child {
  border-bottom: none;
}

.fan-detail__preferred-row--group {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: var(--ds-space-3, 12px);
  margin-top: var(--ds-space-2, 8px);
}

.fan-detail__preferred-info {
  flex: 1;
  min-width: 0;
}

.fan-detail__preferred-rest-name {
  font-weight: 500;
}

.fan-detail__preferred-via {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.fan-detail__preferred-engagement {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--ds-space-3, 12px);
  margin-top: 2px;
}

.fan-detail__preferred-engagement-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.fan-detail__preferred-actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2, 8px);
}

/* Shared by the member (Add to Profile) */
.fan-detail__share-restaurant { font-size: 12px; font-weight: 600; margin-bottom: 2px; }
.fan-detail__share-text { font-size: 14px; line-height: 1.45; white-space: pre-wrap; }
.fan-detail__share-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin-top: 4px; font-size: 12px; opacity: 0.7; }

/* Guest notes */
.fan-detail__notes-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-2, 8px);
}

.fan-detail__notes-head .fan-detail__notes-title {
  flex: 1;
  min-width: 0;
  padding-right: var(--ds-space-2, 8px);
}

.fan-detail__notes-head-action {
  padding-right: var(--ds-space-3, 12px);
  flex-shrink: 0;
}

.fan-detail__notes-body {
  padding-top: 0;
}

.fan-detail__notes-sub {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin: 0 0 var(--ds-space-3, 12px);
}

.fan-detail__notes-empty {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  padding: var(--ds-space-2, 8px) 0;
  margin: 0;
}

.fan-detail__notes-list {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2, 8px);
}

.fan-detail__note-restaurant {
  font-size: 14px;
  font-weight: 600;
}

.fan-detail__note-meta {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

/* Read-mode row — a click target that opens the editor */
.fan-detail__note-read {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: var(--ds-radius-md, 8px);
  padding: var(--ds-space-3, 12px);
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;
  font: inherit;
  color: inherit;
}

.fan-detail__note-read:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.03);
  border-color: rgba(0, 0, 0, 0.16);
}

.fan-detail__note-read:focus-visible {
  outline: 2px solid var(--ds-brand-primary-dark, currentColor);
  outline-offset: 2px;
}

.fan-detail__note-read-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
  margin-bottom: var(--ds-space-1, 4px);
  flex-wrap: wrap;
}

.fan-detail__note-read-body {
  font-size: 14px;
  line-height: 1.45;
  color: rgba(var(--v-theme-on-surface), 0.85);
  white-space: pre-wrap;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* Edit-mode row — replaces the read-mode card in place */
.fan-detail__note-edit {
  background: rgba(var(--v-theme-on-surface), 0.02);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: var(--ds-radius-md, 8px);
  padding: var(--ds-space-3, 12px);
}

.fan-detail__note-edit .fan-detail__note-restaurant {
  margin-bottom: var(--ds-space-2, 8px);
}

.fan-detail__note-edit-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
  margin-top: var(--ds-space-2, 8px);
  flex-wrap: wrap;
}

.fan-detail__note-edit-buttons {
  display: flex;
  gap: var(--ds-space-1, 4px);
}

/* Booking rows */
.fan-detail__booking-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
  padding: var(--ds-space-3, 12px) var(--ds-space-4, 16px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.fan-detail__booking-row:last-child {
  border-bottom: none;
}

.fan-detail__booking-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fan-detail__booking-date {
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-strong, inherit);
}

.fan-detail__booking-rest {
  font-size: 14px;
}

.fan-detail__booking-meta {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.fan-detail__booking-right {
  display: flex;
  align-items: center;
  gap: var(--ds-space-1, 4px);
  flex-shrink: 0;
}

/* Messaging */
.fan-detail__conv-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.fan-detail__messaging-empty {
  padding: var(--ds-space-3, 12px) var(--ds-space-4, 16px);
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
  allowLoggedInOnly: true
  requiresRestaurantAdmin: true
</route>
